/**
 * Clasificación de `EstadoExpediente` en tres clases de "vida del trámite"
 * (ADR 017 — Unicidad del expediente activo por estudiante).
 *
 * - **VIVO**: trámite en curso normal o reabriible por excepción
 *   institucional (incluye `PLAZO_EJECUCION_VENCIDO`, que el Decanato
 *   puede revivir con resolución). Bloquea creación de nuevo expediente.
 *
 * - **CERRADO_REGULAR**: trámite que terminó por su flujo regular
 *   (rechazo institucional, desaprobación, titulación). El estudiante
 *   puede iniciar otro proceso desde cero. NO bloquea.
 *
 * - **CERRADO_BLOQUEANTE**: trámite suspendido por anomalía administrativa
 *   (observado por coordinación, suspendido). Requiere regularización
 *   antes de poder iniciar otro. Bloquea.
 *
 * Patrón `Record<EstadoExpediente, ClaseEstadoExpediente>` con typecheck
 * exhaustivo: agregar un nuevo `EstadoExpediente` al enum sin clasificarlo
 * acá rompe la compilación (OCP forzado por tipos). Esto asegura que toda
 * decisión futura sobre estados nuevos pase por una revisión consciente
 * de su impacto en la regla de unicidad.
 */

import { EstadoExpediente } from "./enums";

export type ClaseEstadoExpediente = "VIVO" | "CERRADO_REGULAR" | "CERRADO_BLOQUEANTE";

export const CLASE_POR_ESTADO: Record<EstadoExpediente, ClaseEstadoExpediente> = {
  // VIVO — trámite en curso normal o reabriible.
  [EstadoExpediente.BORRADOR_SOLICITUD]: "VIVO",
  [EstadoExpediente.ASESORIA_SOLICITADA]: "VIVO",
  [EstadoExpediente.ASESORIA_ACEPTADA]: "VIVO",
  [EstadoExpediente.SOLICITUD_PRESENTADA]: "VIVO",
  [EstadoExpediente.SOLICITUD_EN_REVISION_DECANATO]: "VIVO",
  [EstadoExpediente.SOLICITUD_OBSERVADA]: "VIVO",
  [EstadoExpediente.ASESOR_DESIGNADO]: "VIVO",
  [EstadoExpediente.PROYECTO_EN_ELABORACION]: "VIVO",
  [EstadoExpediente.PROYECTO_EN_REVISION_ASESOR]: "VIVO",
  [EstadoExpediente.PROYECTO_CON_CONFORMIDAD_ASESOR]: "VIVO",
  [EstadoExpediente.JURADO_DESIGNADO]: "VIVO",
  [EstadoExpediente.PROYECTO_EN_REVISION_JURADO]: "VIVO",
  [EstadoExpediente.PROYECTO_OBSERVADO]: "VIVO",
  [EstadoExpediente.PROYECTO_APROBADO]: "VIVO",
  [EstadoExpediente.TESIS_EN_EJECUCION]: "VIVO",
  // Vencido por plazo: el Decanato puede revivirlo con resolución; se
  // mantiene como VIVO (bloquea creación) hasta que se resuelva por
  // EXPEDIENTE_OBSERVADO o TRAMITE_SUSPENDIDO (decisión cliente
  // 2026-05-25).
  [EstadoExpediente.PLAZO_EJECUCION_VENCIDO]: "VIVO",
  [EstadoExpediente.BORRADOR_TESIS_PRESENTADO]: "VIVO",
  [EstadoExpediente.BORRADOR_EN_REVISION_JURADO]: "VIVO",
  [EstadoExpediente.BORRADOR_OBSERVADO]: "VIVO",
  [EstadoExpediente.BORRADOR_SUBSANADO]: "VIVO",
  [EstadoExpediente.BORRADOR_SUFICIENTE]: "VIVO",
  [EstadoExpediente.EN_CONTROL_ANTIPLAGIO]: "VIVO",
  [EstadoExpediente.ANTIPLAGIO_OBSERVADO]: "VIVO",
  [EstadoExpediente.ANTIPLAGIO_APROBADO]: "VIVO",
  [EstadoExpediente.FECHA_SUSTENTACION_SOLICITADA]: "VIVO",
  [EstadoExpediente.SUSTENTACION_PROGRAMADA]: "VIVO",
  [EstadoExpediente.SUSTENTACION_REPROGRAMADA]: "VIVO",
  [EstadoExpediente.SUSTENTACION_REALIZADA]: "VIVO",
  [EstadoExpediente.SUSTENTACION_APROBADA]: "VIVO",
  [EstadoExpediente.TESIS_REGISTRADA_REPOSITORIO]: "VIVO",
  [EstadoExpediente.TITULO_EN_TRAMITE]: "VIVO",

  // CERRADO_REGULAR — terminó por flujo regular; permite reintento.
  [EstadoExpediente.SOLICITUD_RECHAZADA]: "CERRADO_REGULAR",
  [EstadoExpediente.PROYECTO_RECHAZADO]: "CERRADO_REGULAR",
  [EstadoExpediente.BORRADOR_INSUFICIENTE]: "CERRADO_REGULAR",
  [EstadoExpediente.SUSTENTACION_DESAPROBADA]: "CERRADO_REGULAR",
  // Titulación exitosa: el flujo terminó. Si el estudiante quiere otra
  // tesis (segunda especialidad, maestría), crea un expediente nuevo.
  // El sistema no diferencia "primera" de "segunda" titulación; cada
  // expediente es independiente.
  [EstadoExpediente.TITULO_OTORGADO]: "CERRADO_REGULAR",

  // CERRADO_BLOQUEANTE — anomalía que requiere intervención manual.
  [EstadoExpediente.EXPEDIENTE_OBSERVADO]: "CERRADO_BLOQUEANTE",
  [EstadoExpediente.TRAMITE_SUSPENDIDO]: "CERRADO_BLOQUEANTE",
};

/** Devuelve la clase del estado. Type-safe (cubre los 37 estados). */
export function claseDeEstado(estado: EstadoExpediente): ClaseEstadoExpediente {
  return CLASE_POR_ESTADO[estado];
}

/**
 * `true` si tener un expediente en este estado bloquea la creación de un
 * nuevo expediente para el mismo estudiante. Bloquean: `VIVO` y
 * `CERRADO_BLOQUEANTE`. No bloquea: `CERRADO_REGULAR`.
 */
export function bloqueaCreacionDeNuevoExpediente(estado: EstadoExpediente): boolean {
  const clase = claseDeEstado(estado);
  return clase === "VIVO" || clase === "CERRADO_BLOQUEANTE";
}

/**
 * Lista materializada de estados bloqueantes. Útil para queries Prisma
 * (`where: { estadoActual: { in: ESTADOS_BLOQUEANTES_CREACION } }`) y
 * para la migración SQL del índice parcial único (ADR 017 §4.2).
 *
 * Se deriva de `CLASE_POR_ESTADO` en runtime; cualquier cambio en la
 * clasificación se propaga automáticamente. NUNCA hardcodear esta lista
 * en otros archivos.
 */
export const ESTADOS_BLOQUEANTES_CREACION: readonly EstadoExpediente[] = Object.entries(
  CLASE_POR_ESTADO,
)
  .filter(([, clase]) => clase === "VIVO" || clase === "CERRADO_BLOQUEANTE")
  .map(([estado]) => estado as EstadoExpediente);
